import { DateFormatterOptions } from "globalize";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { formatPatientGender } from "../formatUtils";
import { PatientId } from "../messages/ObjectIds";
import { PatientGender } from "../messages/Visit";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { isOvernight } from "../utils/dateUtils";
import {
  CALENDAR_SVG,
  CALL_SVG,
  CHEVRON_RIGHT_SVG,
  CLOCK_SVG,
  MARKER_SVG,
  NAVIGATE_SVG,
  TORSO_SVG,
} from "../utils/svgs";
import { LANGUAGE_ICON } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { AppList, AppListDivider, AppListItem } from "./Generic/AppList";
import Flex from "./Generic/Flex";
import { If } from "./Generic/If";
import { isDefined } from "./Generic/IsDefined";
import { Overnight } from "./Overnight";

interface Props {
  patientName: string;
  patientAge: number | null;
  patientGender: PatientGender;
  patientLanguages: string[];
  patientId: PatientId;
  address: string;
  address2: string | null;
  addressInstructions: string | null;
  startDateTime: LocalDateTime;
  endDateTime: LocalDateTime;
  onClickOpenPatient: (patientId: PatientId) => void;
  onClickCallPatient: (patientId: PatientId) => void;
  onClickDirections: () => void;
}

const VisitInstanceDetailsView = (props: Props) => {
  const isVisitOvernight = isOvernight(props.startDateTime, props.endDateTime);
  const address = splitAddressToMacroMicro(props.address, props.address2);

  const patientInformation = [
    props.patientAge !== null
      ? `${props.patientAge} ${translate(STRINGS.YEARS_OLD)}`.toLowerCase()
      : null,
    props.patientGender !== null ? formatPatientGender(props.patientGender).toLowerCase() : null,
  ]
    .filter(isDefined)
    .join(", ");

  const handleClickCallPatient = () => {
    return props.onClickCallPatient(props.patientId);
  };

  const handleClickOpenPatient = () => {
    return props.onClickOpenPatient(props.patientId);
  };

  return (
    <AppCard className="VisitInstanceDetailsView">
      <AppCardHeader>
        <AppCardTitle>{translate(STRINGS.VISIT_DETAILS)}</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <AppList>
          <AppListItem
            leading={<AppIcon icon={TORSO_SVG} />}
            title={props.patientName}
            trailing={
              <AppButton size="small" variant="outline" className="UserDetailsButton">
                {translate(STRINGS.DETAILS)}
                <AppIcon icon={CHEVRON_RIGHT_SVG} size={12} />
              </AppButton>
            }
            onClick={handleClickOpenPatient}
            extra={<AppListItem title={patientInformation} soft={true} />}
          />

          <AppListItem
            leading={<AppIcon icon={CALENDAR_SVG} />}
            title={formatDate(props.startDateTime, "short")}
          />

          <AppListItem
            leading={<AppIcon icon={CLOCK_SVG} />}
            title={renderVisitTimeRangeTitle(props.startDateTime, props.endDateTime)}
            trailing={<Overnight isOvernight={isVisitOvernight} />}
          />

          <AppListItem
            leading={<AppIcon icon={MARKER_SVG} />}
            title={
              <strong>
                {address.micro} {address.macro}
              </strong>
            }
            extra={
              <>
                {props.addressInstructions !== null &&
                  props.addressInstructions.length > 1 &&
                  `${translate(STRINGS.DIRECTIONS)}:  ${props.addressInstructions}`}
                <AppButton onClick={props.onClickDirections} size="small">
                  {translate(STRINGS.OPEN_MAPS)}
                </AppButton>
              </>
            }
          />

          <If condition={props.patientLanguages.length > 0}>
            <AppListItem
              leading={<AppIcon icon={LANGUAGE_ICON} fill="#52606c" />}
              title={props.patientLanguages.join(", ")}
            />
          </If>

          <AppListDivider />
        </AppList>

        <Flex xGap={16} alignItems="center" stretchItems={true}>
          <AppButton variant="outline" size="small" onClick={handleClickCallPatient}>
            <AppIcon icon={CALL_SVG} size={16} />
            <span>{translate(STRINGS.CALL_PATIENT)}</span>
          </AppButton>

          <AppButton size="small" onClick={props.onClickDirections}>
            <AppIcon icon={NAVIGATE_SVG} size={16} />
            <span>{translate(STRINGS.DIRECTIONS)}</span>
          </AppButton>
        </Flex>
      </AppCardContent>
    </AppCard>
  );
};

function splitAddressToMacroMicro(address: string, address2: string | null) {
  const [microPlace, ...macroPlaces] = address.split(", ");
  const micro = isDefined(address2) ? `${microPlace}, ${address2}` : microPlace;
  const macro = macroPlaces.join(", ");

  return {
    macro: macro,
    micro: micro,
  };
}

function formatDate(dateTime: LocalDateTime, date: DateFormatterOptions["date"]) {
  return globalize().formatLocalDateTime(dateTime, { date: date });
}

function renderVisitTimeRangeTitle(startTime: LocalDateTime, endTime: LocalDateTime) {
  const formatterStartTime = globalize().formatLocalDateTime(startTime, { time: "short" });
  const formatterEndTime = globalize().formatLocalDateTime(endTime, { time: "short" });

  return `${formatterStartTime} - ${formatterEndTime}`;
}

export default VisitInstanceDetailsView;
